import { Option } from 'src/shared/form/control'
import {
  ConfigurationGridDataSettings,
  ConfigurationTablePageSortSettings,
  DEFAULT_CONFIG_GRID_DATA_SETTINGS,
  DEFAULT_CONFIG_TABLE_PAGE_SORT_SETTINGS,
} from 'src/shared/constants/table-settings-constants'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { useClient } from 'urql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useUserLocale } from 'src/user/UserContext'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'
import { Feature_Config_Bool_Exp, Query_Root } from 'src/@types/graphql'
import { queryFeatureConfigs } from 'src/screens/administration/configuration-management/features-configuration/features/featuresConfigQueries'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { Autocomplete, Box, Button, Grid2, Stack, TextField, Typography } from '@mui/material'
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined'
import {
  deleteFeatureConfig,
  fetchAllFeatureTypes,
  mapAllFeatureTypesToOptions,
} from 'src/screens/administration/configuration-management/features-configuration/features/FeaturesConfigGridUtils'
import { FeaturesConfigGrid } from 'src/screens/administration/configuration-management/features-configuration/features/FeaturesConfigGrid'
import {
  SpecificConfigFilter,
  useFilterSettingsFromLocalStorage,
  useTableSettingsFromLocalStorage,
} from 'src/screens/administration/configuration-management/ConfigurationStorageUtils'
import {
  getConfigStatusOptions,
  getOptionsForFilter,
  getTranslationForFeatureType,
  getTranslationForStatus,
} from 'src/screens/administration/configuration-management/ConfigurationUtils'
import {
  getFeaturesConfigProcessOptions,
  getTranslationForFeatureProcess,
} from 'src/screens/administration/configuration-management/features-configuration/FeaturesConfigurationUtils'
import { SecondaryConfirmationModalDialog } from 'src/shared/modal-dialog/SecondaryConfirmationModalDialog'

const FEATURE_CONFIG_TABLE_NAME = 'FEATURE_CONFIG'

type FeaturesConfigFilters = SpecificConfigFilter<'FEATURE_CONFIG'>

const defaultFeaturesConfigTableSettings: ConfigurationTablePageSortSettings = {
  ...DEFAULT_CONFIG_TABLE_PAGE_SORT_SETTINGS,
}

export const FeaturesConfigurationFeaturesPage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const locale = useUserLocale()

  // filter options
  const [featuresConfigTypeOptions, setFeaturesConfigTypeOptions] = useState<Option[]>(getOptionsForFilter([]))

  const featuresProcessOptions: Option[] = useMemo(
    () => getOptionsForFilter(getFeaturesConfigProcessOptions(getMessage)),
    [getMessage],
  )
  const featuresConfigStatusOptions: Option[] = useMemo(
    () => getOptionsForFilter(getConfigStatusOptions(getMessage)),
    [getMessage],
  )

  const defaultFeaturesConfigFiltersValues: FeaturesConfigFilters = useMemo(
    () => ({
      featuresProcessFilter: featuresProcessOptions[0],
      featuresTypeFilter: featuresConfigTypeOptions[0],
      featuresStatusFilter: featuresConfigStatusOptions[0],
    }),
    [featuresProcessOptions, featuresConfigStatusOptions, featuresConfigTypeOptions],
  )

  // grid and filter settings
  const [gridDataSettings, setGridDataSettings] = useState<ConfigurationGridDataSettings>(
    DEFAULT_CONFIG_GRID_DATA_SETTINGS,
  )
  const [tableSettings, setTableSettings] = useTableSettingsFromLocalStorage(
    FEATURE_CONFIG_TABLE_NAME,
    defaultFeaturesConfigTableSettings,
  )
  const [featuresConfigFilters, setFeaturesConfigFilters] = useFilterSettingsFromLocalStorage(
    defaultFeaturesConfigFiltersValues,
  )

  // delete modal state
  const [deleteModalLoading, setDeleteModalLoading] = useState(false)
  const [featureToBeDeleted, setFeatureToBeDeleted] = useState<number | null>(null)

  // table state
  const fetchRows = useCallback(async () => {
    const limit = tableSettings.paginationModel.pageSize
    const offset = tableSettings.paginationModel.page * tableSettings.paginationModel.pageSize

    const featuresConfigCondition: Feature_Config_Bool_Exp = { _and: [] }
    if (featuresConfigFilters.featuresProcessFilter.value != null) {
      const selectedProcess = featuresConfigFilters.featuresProcessFilter.value as string
      featuresConfigCondition._and?.push({ processes: { _contains: selectedProcess } })
    }
    if (featuresConfigFilters.featuresTypeFilter.value != null) {
      const featureTypeConfigId = featuresConfigFilters.featuresTypeFilter.value as number
      featuresConfigCondition._and?.push({ feature_type_id: { _eq: featureTypeConfigId } })
    }
    if (featuresConfigFilters.featuresStatusFilter.value != null) {
      const isActive = featuresConfigFilters.featuresStatusFilter.value === CONFIGURATION_STATUS.ACTIVE
      featuresConfigCondition._and?.push({ active: { _eq: isActive } })
    }

    const { data, error } = await urqlClient
      .query<
        {
          feature_config: Query_Root['feature_config']
          feature_config_aggregate: Query_Root['feature_config_aggregate']
        },
        {
          featuresConfigCondition: Feature_Config_Bool_Exp
          offset: number
          limit: number
        }
      >(queryFeatureConfigs, {
        featuresConfigCondition,
        offset,
        limit,
      })
      .toPromise()

    if (error || !data?.feature_config || !data.feature_config_aggregate) {
      throw new Error('Resource not found')
    }

    const fetchedRows = data.feature_config.map((fc) => ({
      id: fc.id,
      name: fc.feature_config_name_locale,
      feature_type: fc.feature_type_config.names[locale],
      feature_group: fc.feature_group_config?.names[locale],
      allocation: fc.processes,
      status: fc.active,
      is_deletable: !!fc.feature_deletability?.is_deletable,
    }))
    const fetchedRowsCount = data.feature_config_aggregate.aggregate?.count ?? 0

    return {
      fetchedRows,
      fetchedRowsCount,
    }
  }, [featuresConfigFilters, tableSettings.paginationModel, locale, urqlClient])

  const initRows = useCallback(() => {
    setGridDataSettings((prevGridData) => ({
      ...prevGridData,
      gridLoading: true,
    }))

    fetchRows()
      .then((res) => {
        setGridDataSettings({
          rows: res.fetchedRows,
          rowsCount: res.fetchedRowsCount,
          gridLoading: false,
        })
      })
      .catch((_) => {
        notificationService.operationFailed()
        setGridDataSettings((prevDataSettings) => ({
          ...prevDataSettings,
          gridLoading: false,
        }))
      })
  }, [fetchRows, notificationService])

  // filter handlers
  const onFeaturesConfigProcessChange = useCallback(
    (newValue: Option | null) => {
      setTableSettings({
        ...tableSettings,
        paginationModel: { ...tableSettings.paginationModel, page: 0 },
      })
      setFeaturesConfigFilters({
        ...featuresConfigFilters,
        featuresProcessFilter: newValue ?? defaultFeaturesConfigFiltersValues.featuresProcessFilter,
      })
    },
    [
      tableSettings,
      featuresConfigFilters,
      defaultFeaturesConfigFiltersValues.featuresProcessFilter,
      setTableSettings,
      setFeaturesConfigFilters,
    ],
  )

  const onFeaturesConfigFeatureTypeChange = useCallback(
    (newValue: Option | null) => {
      setTableSettings({
        ...tableSettings,
        paginationModel: { ...tableSettings.paginationModel, page: 0 },
      })
      setFeaturesConfigFilters({
        ...featuresConfigFilters,
        featuresTypeFilter: newValue ?? defaultFeaturesConfigFiltersValues.featuresTypeFilter,
      })
    },
    [
      tableSettings,
      featuresConfigFilters,
      defaultFeaturesConfigFiltersValues.featuresTypeFilter,
      setTableSettings,
      setFeaturesConfigFilters,
    ],
  )

  const onFeaturesConfigStatusChange = useCallback(
    (newValue: Option | null) => {
      setTableSettings({
        ...tableSettings,
        paginationModel: { ...tableSettings.paginationModel, page: 0 },
      })
      setFeaturesConfigFilters({
        ...featuresConfigFilters,
        featuresStatusFilter: newValue ?? defaultFeaturesConfigFiltersValues.featuresStatusFilter,
      })
    },
    [
      tableSettings,
      featuresConfigFilters,
      defaultFeaturesConfigFiltersValues.featuresStatusFilter,
      setTableSettings,
      setFeaturesConfigFilters,
    ],
  )

  const handleResetFilters = useCallback(() => {
    setFeaturesConfigFilters(defaultFeaturesConfigFiltersValues)
    setTableSettings({
      ...tableSettings,
      paginationModel: { ...tableSettings.paginationModel, page: 0 },
    })
  }, [defaultFeaturesConfigFiltersValues, tableSettings, setFeaturesConfigFilters, setTableSettings])

  // delete modal handlers
  const handleCancelDelete = useCallback(() => {
    setFeatureToBeDeleted(null)
  }, [])

  const handleDeleteConfirmed = useCallback(() => {
    if (!featureToBeDeleted) {
      notificationService.operationFailed()
      return
    }

    setDeleteModalLoading(true)
    deleteFeatureConfig(urqlClient)(featureToBeDeleted)
      .then(() => {
        notificationService.deleteSuccessful()
        initRows()
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
      .finally(() => {
        setDeleteModalLoading(false)
        setFeatureToBeDeleted(null)
      })
  }, [featureToBeDeleted, initRows, notificationService, urqlClient])

  useEffect(() => {
    fetchAllFeatureTypes(urqlClient, locale)()
      .then(({ fetchedFeatureTypes }) => {
        const mappedOptionsOfFeatureTypes = getOptionsForFilter(
          mapAllFeatureTypesToOptions(fetchedFeatureTypes, locale),
        )

        setFeaturesConfigTypeOptions(mappedOptionsOfFeatureTypes)
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
  }, [urqlClient, locale, notificationService, getMessage])

  useEffect(() => {
    initRows()
  }, [initRows])

  return (
    <>
      <ScreenLayout title={`${getMessage('label.configuration.features')} ${getMessage('label.configuration')}`}>
        <PageLayout>
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 1,
              padding: 2,
              marginBottom: 2,
            }}
          >
            <Stack spacing={2}>
              <Typography variant="body1" color="text.secondary" fontWeight="bold">
                {getMessage('label.grid.filter')}
              </Typography>
              <Grid2 container spacing={2}>
                <Grid2 size={4}>
                  <Autocomplete
                    options={featuresProcessOptions}
                    value={featuresConfigFilters.featuresProcessFilter}
                    onChange={(_, newVal) => onFeaturesConfigProcessChange(newVal)}
                    getOptionLabel={(opt) => getTranslationForFeatureProcess(getMessage, opt.value)}
                    renderInput={(params) => <TextField {...params} label={getMessage('label.process')} />}
                  />
                </Grid2>
                <Grid2 size={4}>
                  <Autocomplete
                    options={featuresConfigTypeOptions}
                    value={featuresConfigFilters.featuresTypeFilter}
                    onChange={(_, newVal) => onFeaturesConfigFeatureTypeChange(newVal)}
                    getOptionLabel={(opt) =>
                      getTranslationForFeatureType(getMessage, opt.value, featuresConfigTypeOptions)
                    }
                    renderInput={(params) => <TextField {...params} label={getMessage('label.feature.type')} />}
                  />
                </Grid2>
                <Grid2 size={4}>
                  <Autocomplete
                    options={featuresConfigStatusOptions}
                    value={featuresConfigFilters.featuresStatusFilter}
                    onChange={(_, newVal) => onFeaturesConfigStatusChange(newVal)}
                    getOptionLabel={(opt) => getTranslationForStatus(getMessage, opt.value)}
                    renderInput={(params) => <TextField {...params} label={getMessage('label.status')} />}
                  />
                </Grid2>
              </Grid2>
              <Grid2 container>
                <Grid2 size={4}>
                  <Button variant="text" startIcon={<FilterAltOffOutlinedIcon />} onClick={handleResetFilters}>
                    {getMessage('label.configuration.criteria.group.grid.filter.reset.button')}
                  </Button>
                </Grid2>
              </Grid2>
            </Stack>
          </Box>
          <FeaturesConfigGrid
            dataSettings={gridDataSettings}
            tableSettings={tableSettings}
            setTableSettings={setTableSettings}
            setFeatureToBeDeleted={setFeatureToBeDeleted}
          />
        </PageLayout>
      </ScreenLayout>

      <SecondaryConfirmationModalDialog
        open={!!featureToBeDeleted}
        onCancel={handleCancelDelete}
        onConfirm={handleDeleteConfirmed}
        loading={deleteModalLoading}
        buttonColor="error"
      >
        <Box paddingRight={1}>
          <Typography variant="subtitle1">
            {getMessage('label.configuration.features.grid.delete.confirm.body')}
          </Typography>
        </Box>
      </SecondaryConfirmationModalDialog>
    </>
  )
}
